import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ActionProposal, ConfirmActionRequest } from '../../../services/assistantCentral';
import { friendlyAssistantError } from '../../../services/assistantCentral';
import { ASSISTANT_THEME } from '../assistantTheme';

const REVERSIBLE_LABEL: Record<ActionProposal['reversible'], string> = {
  sim: 'Pode ser desfeita',
  parcial: 'Desfazer exige outro ajuste',
  nao: 'Não pode ser desfeita',
};

const STATUS_LABEL: Record<string, { label: string; color: 'success' | 'error' | 'default' | 'warning' }> = {
  executed: { label: 'Executada', color: 'success' },
  failed: { label: 'Falhou', color: 'error' },
  expired: { label: 'Expirada', color: 'default' },
  cancelled: { label: 'Cancelada', color: 'default' },
};

interface ActionProposalCardProps {
  proposal: ActionProposal;
  onConfirm: (id: string, body: ConfirmActionRequest) => Promise<void>;
  onCancel: (id: string) => Promise<void>;
}

const ActionProposalCard: React.FC<ActionProposalCardProps> = ({ proposal, onConfirm, onCancel }) => {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const expired = proposal.status === 'proposed' && new Date(proposal.expires_at).getTime() < Date.now();
  const pending = proposal.status === 'proposed' && !expired;
  const finalStatus = expired ? STATUS_LABEL.expired : STATUS_LABEL[proposal.status];
  const reasonOk = !proposal.requires_reason || reason.trim().length >= 5;
  const valueOk = !proposal.confirm_value || typed.trim().length > 0;

  const handleConfirm = async () => {
    setBusy('confirm');
    setError(null);
    try {
      await onConfirm(proposal.id, {
        ...(proposal.requires_reason ? { reason: reason.trim() } : {}),
        ...(proposal.confirm_value ? { typed_value: typed.trim() } : {}),
      });
    } catch (e) {
      setError(friendlyAssistantError(e));
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async () => {
    setBusy('cancel');
    setError(null);
    try {
      await onCancel(proposal.id);
    } catch (e) {
      setError(friendlyAssistantError(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      sx={{
        mt: 1,
        p: 1.25,
        borderRadius: 2,
        bgcolor: ASSISTANT_THEME.paper,
        border: `1px solid ${proposal.risk === 'N3' ? '#FDE68A' : ASSISTANT_THEME.borderSoft}`,
        boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: ASSISTANT_THEME.navy, flex: 1, minWidth: 0 }}>
          {proposal.title}
        </Typography>
        {proposal.risk === 'N3' && <Chip size="small" color="warning" label="Sensível" sx={{ height: 20, fontSize: '0.68rem' }} />}
        {finalStatus && <Chip size="small" color={finalStatus.color} label={finalStatus.label} sx={{ height: 20, fontSize: '0.68rem' }} />}
      </Box>
      {proposal.description && (
        <Typography sx={{ fontSize: '0.76rem', color: ASSISTANT_THEME.slateMid, mb: 0.75 }}>{proposal.description}</Typography>
      )}

      {proposal.preview?.lines?.length > 0 && (
        <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.25, mb: 0.75 }}>
          {proposal.preview.lines.map((line, i) => (
            <React.Fragment key={`${line.label}-${i}`}>
              <Typography component="dt" sx={{ fontSize: '0.72rem', color: ASSISTANT_THEME.slate }}>
                {line.label}
              </Typography>
              <Typography component="dd" sx={{ m: 0, fontSize: '0.74rem', fontWeight: 600, color: ASSISTANT_THEME.navy }}>
                {line.value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
      )}

      {proposal.preview?.warnings?.map((w, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', mb: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 15, color: 'warning.main', mt: '2px' }} />
          <Typography sx={{ fontSize: '0.72rem', color: '#92400E' }}>{w}</Typography>
        </Box>
      ))}

      <Typography sx={{ fontSize: '0.68rem', color: ASSISTANT_THEME.slateFaint, mb: pending ? 0.75 : 0 }}>
        {REVERSIBLE_LABEL[proposal.reversible]}
      </Typography>

      {pending && (
        <>
          {proposal.requires_reason && (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Motivo (obrigatório)"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              sx={{ mb: 0.75, '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
            />
          )}
          {proposal.confirm_value && (
            <TextField
              size="small"
              fullWidth
              label={`Digite ${proposal.confirm_value} para confirmar`}
              value={typed}
              onChange={(e) => setTyped(e.target.value.slice(0, 40))}
              sx={{ mb: 0.75, '& .MuiInputBase-input': { fontSize: '0.78rem' } }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleCancel} disabled={busy !== null} sx={{ textTransform: 'none' }}>
              {busy === 'cancel' ? <CircularProgress size={14} /> : 'Cancelar'}
            </Button>
            <Button
              size="small"
              variant="contained"
              color={proposal.risk === 'N3' ? 'warning' : 'primary'}
              onClick={handleConfirm}
              disabled={busy !== null || !reasonOk || !valueOk}
              sx={{ textTransform: 'none' }}
            >
              {busy === 'confirm' ? <CircularProgress size={14} color="inherit" /> : 'Confirmar'}
            </Button>
          </Box>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 0.75, py: 0, fontSize: '0.74rem' }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ActionProposalCard;
